import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { plansApi, type OnchainPayment } from "@/api/plans";
import { projectsApi } from "@/api/projects";
import { Copy, Check, CheckCircle2 } from "lucide-react";
import { PayWidget } from "@/components/pay/PayWidget";

export const Route = createFileRoute("/app/projects/$pid/pay/$paymentId")({
  component: PayPage,
});

// FE refresh cadence. The server stamps a 20-min expires_at on every
// POST; we silently re-POST every 15 min so the watcher never stops
// while the user is on the page.
const POLL_MS = 5_000;
const REFRESH_WATCH_MS = 15 * 60_000;

function PayPage() {
  const { pid, paymentId } = Route.useParams();
  const qc = useQueryClient();
  const [showCopied, setShowCopied] = useState(false);

  // Initial fetch — if no onchain_payment exists yet, this 404s and
  // we fall through to creating one via the mutation below.
  const { data, isError } = useQuery<OnchainPayment>({
    queryKey: ["onchain", pid, paymentId],
    queryFn: () => plansApi.onchainGet(pid, paymentId),
    refetchInterval: POLL_MS,
    retry: false,
  });

  const { data: project } = useQuery({
    queryKey: ["project", pid],
    queryFn: () => projectsApi.get(pid),
  });

  const requestMut = useMutation({
    mutationFn: () => plansApi.onchainRequest(pid, paymentId),
    onSuccess: (oc) => {
      qc.setQueryData(["onchain", pid, paymentId], oc);
    },
  });

  // If the GET 404'd, mint the onchain_payment.
  useEffect(() => {
    if (isError && !requestMut.isPending) requestMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Silently bump expires_at every 15 min while the page is open.
  useEffect(() => {
    if (!data || data.status !== "pending") return;
    const t = setInterval(() => requestMut.mutate(), REFRESH_WATCH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.status]);

  const oc = data;
  const isReceived = oc?.status === "received";
  const expiresAt = oc ? new Date(oc.expires_at) : null;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const remainingMs = expiresAt ? expiresAt.getTime() - now.getTime() : 0;
  const expired = remainingMs <= 0;

  async function copyAddress() {
    if (!oc) return;
    await navigator.clipboard.writeText(oc.public_key);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1500);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold">Pay invoice</h1>
        <p className="mt-1 text-ink/65">
          Pay {oc?.amount_usdc ?? "—"} USDC on Solana. Detection is automatic within
          5 seconds of confirmation.
        </p>
      </div>

      {!oc ? (
        <div className="card">Loading payment address…</div>
      ) : isReceived ? (
        <div className="card border-l-4 border-l-mint-500">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={24} className="mt-0.5 text-mint-500" />
            <div>
              <div className="text-lg font-semibold">Payment received</div>
              <div className="mt-1 text-sm text-ink/65">
                Received {oc.received_amount} at {oc.received_at && new Date(oc.received_at).toLocaleTimeString()}.
                Your account has been credited.
              </div>
              <Link to="/app/projects/$pid" params={{ pid }} className="btn-primary mt-4 inline-block">
                Back to project
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          <PayWidget
            recipient={oc.public_key}
            amountUsdc={oc.amount_usdc}
            projectName={project?.name ?? "your project"}
            paymentId={paymentId}
          />

          <div className="card space-y-3">
            <div className="text-xs font-medium uppercase tracking-wider text-ink/55">
              Or send manually
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg bg-ink/4 p-3 font-mono text-xs">
                {oc.public_key}
              </code>
              <button
                onClick={copyAddress}
                className="rounded-md border border-ink/15 p-2 hover:bg-cream"
                title="Copy address"
              >
                {showCopied ? <Check size={16} className="text-mint-500" /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-xs text-ink/55">
              This is a one-time address dedicated to this invoice. Send only USDC on
              Solana — other tokens or networks will be lost.
            </p>
            <div className="flex items-baseline justify-between border-t border-ink/8 pt-3 text-sm">
              <div className="text-ink/55">
                Watching for {expired ? "—" : formatRemaining(remainingMs)}
              </div>
              {expired && (
                <button
                  onClick={() => requestMut.mutate()}
                  disabled={requestMut.isPending}
                  className="btn-primary"
                >
                  {requestMut.isPending ? "Resuming…" : "Resume watching"}
                </button>
              )}
            </div>
          </div>

          {expired && (
            <div className="card border-l-4 border-l-peach-500 text-sm text-ink/75">
              Watch window paused — we've stopped polling for new deposits. The address still
              works; click <b>Resume watching</b> to start checking again for 20 more minutes.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
